import 'mapbox-gl/dist/mapbox-gl.css';

import * as React from 'react';

import mapboxgl, { EventData, MapLayerEventType } from 'mapbox-gl';
import ReactMapboxGl, { ZoomControl } from 'react-mapbox-gl';
import { useMedia } from 'react-use';
import styled from 'styled-components';

import { MAPBOX_API_KEY } from '../../../../constants';

import { isBrowser } from '../../../../utils/isBrowser';

import { theme } from '../../../../styles/2020/theme';

import { Section } from '../../common/Primitives';

const MapBox =
  isBrowser() && MAPBOX_API_KEY ? ReactMapboxGl({ accessToken: MAPBOX_API_KEY }) : () => <div />;

type MapBoxBlockProps = React.ComponentProps<typeof Section>;

export const MapBoxBlock = (props: MapBoxBlockProps) => {
  const isPhone = useMedia(`(max-width: ${theme.breakpoints.s})`, true);

  return (
    <MapBoxContainer {...props}>
      <MapBox
        style="mapbox://styles/seanhurley01/ck6zn48bi1xum1iryclh8d0it"
        onStyleLoad={onMapLoad}
        center={[140.541578, -27.446216]}
        zoom={isPhone ? [1.5] : [3]}
      >
        <ZoomControl />
      </MapBox>
    </MapBoxContainer>
  );
};

function createMapPopup(
  e: MapLayerEventType['mouseenter'] & EventData,
  map: mapboxgl.Map,
  popup: mapboxgl.Popup
) {
  if (!e.features?.length) {
    return;
  }

  map.getCanvas().style.cursor = 'pointer';

  const { properties, geometry } = e.features[0];

  if (properties) {
    const coordinates = (geometry as GeoJSON.Point).coordinates.slice() as [number, number];

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    popup.setLngLat(coordinates).setHTML(`<h3>${properties.title}</h3>`).addTo(map);
  }
}

function onMapLoad(map: mapboxgl.Map) {
  if (!isBrowser()) {
    return;
  }

  // Disable scroll-zoom
  map.scrollZoom.disable();

  // School label hover
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
  });

  map.on('mouseenter', 'stile-schools', (e) => {
    createMapPopup(e, map, popup);
  });

  map.on('mouseleave', 'stile-schools', () => {
    map.getCanvas().style.cursor = '';
    popup.remove();
  });
}

const MapBoxContainer = styled(Section)`
  .mapboxgl-map {
    width: 100%;
    height: 300px;

    @media ${theme.media.min.s} {
      padding-bottom: 56.25%;
      height: 100%;
    }
  }

  .mapboxgl-popup {
    max-width: none !important;
    user-select: none;

    .mapboxgl-popup-content {
      max-width: 240px;
      box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.2);
      padding: 12px 24px 10px;
      text-align: center;

      h3 {
        font-size: ${theme.fontSizes.s};
        font-weight: 500;
      }
    }
  }
`;

export default MapBoxBlock;
